import { DEFAULT_TABLE_PAGE_SIZE, roleLabel } from "AppConstants";
import React, { useMemo } from "react";
import PropTypes from "prop-types";

import { DataGrid, Toolbar } from "@mui/x-data-grid";
import MDBox from 'components/MDBox';
import { isEmpty } from "utils/validationUtil";
import { Box, Button, IconButton, Tooltip } from "@mui/material";
import { Edit, Add as AddIcon, Delete as DeleteIcon } from "components/icons";
import { useResizableColumnWidths } from "app/hooks/useResizableColumnWidths";
import { checkUserHasAdminRole } from "app/services/app.state.service";
import { canModifyUser, PRIVILEGED_ROW_HINT } from "utils/userRolePolicy";
import { GridQuickSearchField } from "app/components/common/GridQuickSearchField";

const PAGE_SIZE_OPTIONS = [10, 25, 50, 100];

export const UsersGridToolbar = ({ onAddUser }) => (
    <Toolbar>
        <Box sx={{ py: 2.5, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, width: '100%' }}>
            {onAddUser && (
                <Button
                    startIcon={<AddIcon />}
                    onClick={onAddUser}
                    sx={{ width: 140 }}
                    variant="contained"
                    color="success"
                >
                    Add User
                </Button>
            )}
            <GridQuickSearchField />
        </Box>
    </Toolbar>
);

UsersGridToolbar.propTypes = {
    onAddUser: PropTypes.func,
};

// Frontend grid field name → backend sortBy column (allowlisted server-side).
// A field missing from this map silently falls back to 'email' (see handleSortModelChange),
// so every sortable column MUST have an entry here.
// Exported so tests can assert that invariant directly.
export const SORT_FIELD_MAP = {
    id: 'id',
    fullName: 'fullName',
    email: 'email',
    type: 'type',
    status: 'status',
    // @since NEXT_VERSION
    employeeCode: 'employeeCode',
    departmentName: 'departmentName',
};

export const UsersDataGrid = (props) => {
    const {
        users,
        totalElements,
        page,
        pageSize,
        sortBy,
        sortDirection,
        searchQuery,
        isLoading,
        onPaginationModelChange,
        onSortModelChange,
        onSearchChange,
        onAddUser,
        editEventHandler,
        confirmDeleteDialogHandler,
    } = props;

    const callerIsAdmin = checkUserHasAdminRole();

    const columns = [
        {
            field: "actions",
            type: "actions",
            headerName: "Actions",
            width: 100,
            sortable: false,
            cellClassName: "actions",
            // A Master Data Admin may look at an Admin record but not act on it, so the row is read
            // only for everyone except an Admin (HRMS-263). Disabled rather than hidden, with the
            // reason on hover, so the restriction does not read as a missing button. The server
            // refuses the same calls in RoleAssignmentPolicy.
            getActions: (obj) => {
                const mayModify = canModifyUser(obj.row, callerIsAdmin);
                const hint = mayModify ? "" : PRIVILEGED_ROW_HINT;
                return [
                    <Tooltip title={hint}>
                        <span>
                            <IconButton aria-label="Edit" disabled={!mayModify} onClick={() => editEventHandler(obj.row)} color="inherit">
                                <Edit />
                            </IconButton>
                        </span>
                    </Tooltip>,
                    <Tooltip title={hint}>
                        <span>
                            <IconButton aria-label="Delete" disabled={!mayModify} onClick={() => confirmDeleteDialogHandler(obj.row)} color="inherit">
                                <DeleteIcon />
                            </IconButton>
                        </span>
                    </Tooltip>,
                ];
            },
        },
        { field: 'id', headerName: 'Id', width: 120 },
        { field: 'fullName', headerName: 'Name', width: 250 },
        // @since NEXT_VERSION
        { field: 'employeeCode', headerName: 'Emp Code', width: 140 },
        { field: 'email', headerName: 'Email', width: 280 },
        { field: 'type', headerName: 'Type', width: 140 },
        // @since NEXT_VERSION
        { field: 'departmentName', headerName: 'Department', width: 180 },
        {
            field: 'roles',
            headerName: 'Roles',
            width: 220,
            sortable: false,
            // v8 signature: (value, row, column, apiRef). Null-safe.
            valueGetter: (value, row) => {
                const roles = row?.roles;
                if (!Array.isArray(roles) || roles.length === 0) return '';
                return roles.map((r) => roleLabel(r?.role)).filter(Boolean).join(', ');
            },
        },
        { field: 'status', headerName: 'Status', width: 140 },
    ];

    // Keeps a dragged column width alive across re-renders — `columns` is rebuilt inline on
    // each render, which would otherwise reset it.
    const { columns: sizedColumns, onColumnWidthChange } = useResizableColumnWidths(columns);

    const getData = () => (isEmpty(users) ? [] : users);

    const handleSortModelChange = (model) => {
        if (!onSortModelChange) return;
        if (!model || model.length === 0) {
            onSortModelChange({ sortBy: 'fullName', sortDirection: 'ASC' });
            return;
        }
        const { field, sort } = model[0];
        const mappedField = SORT_FIELD_MAP[field] || 'email';
        onSortModelChange({
            sortBy: mappedField,
            sortDirection: (sort || 'desc').toUpperCase(),
        });
    };

    // Stable references for controlled DataGrid props. MUI X v8 treats prop identity
    // changes on `sortModel`/`paginationModel` as real state changes, which fires a
    // spurious `sortModelChange` → `navigateToStart` → `setPage(0)` chain after every
    // legitimate page change.
    const sortModel = useMemo(
        () => sortBy && SORT_FIELD_MAP[sortBy]
            ? [{ field: sortBy, sort: (sortDirection || 'DESC').toLowerCase() }]
            : [],
        [sortBy, sortDirection]
    );

    const paginationModel = useMemo(
        () => ({ page: page ?? 0, pageSize: pageSize ?? DEFAULT_TABLE_PAGE_SIZE }),
        [page, pageSize]
    );

    // Controlled filter model mirrors the searchQuery from Redux so the input
    // value persists across re-renders and parent state changes (same stable-
    // reference pattern as sortModel/paginationModel above).
    const filterModel = useMemo(
        () => ({ items: [], quickFilterValues: searchQuery ? [searchQuery] : [] }),
        [searchQuery]
    );

    const handleFilterModelChange = (model) => {
        if (!onSearchChange) return;
        const nextQuery = (model?.quickFilterValues ?? []).join(' ').trim();
        onSearchChange(nextQuery);
    };

    // Memoize so the toolbar slot does not see a new props object on every parent
    // render — keeps MUI X from re-running the toolbar tree unnecessarily.
    const toolbarSlotProps = useMemo(() => ({ onAddUser }), [onAddUser]);

    return (
        <MDBox py={1.5} px={2} height={500}>
            <DataGrid
                density="compact"
                rows={getData()}
                columns={sizedColumns}
                onColumnWidthChange={onColumnWidthChange}
                loading={isLoading}
                paginationMode="server"
                /* While the first fetch is in flight, totalElements is still 0.
                 * Passing rowCount=0 makes MUI think any page > 0 is "out of bounds"
                 * and fires a phantom onPaginationModelChange({page:0}) that clobbers
                 * legitimate user clicks. Sentinel -1 tells MUI "total unknown — don't
                 * validate page bounds". Switch to the real total once we have it. */
                rowCount={totalElements > 0 ? totalElements : -1}
                paginationModel={paginationModel}
                onPaginationModelChange={onPaginationModelChange}
                pageSizeOptions={PAGE_SIZE_OPTIONS}

                sortingMode="server"
                sortModel={sortModel}
                onSortModelChange={handleSortModelChange}

                filterMode="server"
                filterModel={filterModel}
                onFilterModelChange={handleFilterModelChange}

                showToolbar
                slots={{ toolbar: UsersGridToolbar }}
                slotProps={{ toolbar: toolbarSlotProps }}

                initialState={{ columns: { columnVisibilityModel: { id: false } } }}
                disableRowSelectionOnClick
            />
        </MDBox>
    );
};

UsersDataGrid.propTypes = {
    users: PropTypes.array.isRequired,
    totalElements: PropTypes.number,
    page: PropTypes.number,
    pageSize: PropTypes.number,
    sortBy: PropTypes.string,
    sortDirection: PropTypes.string,
    searchQuery: PropTypes.string,
    isLoading: PropTypes.bool,
    onPaginationModelChange: PropTypes.func.isRequired,
    onSortModelChange: PropTypes.func.isRequired,
    onSearchChange: PropTypes.func,
    onAddUser: PropTypes.func,
    editEventHandler: PropTypes.func.isRequired,
    confirmDeleteDialogHandler: PropTypes.func.isRequired,
};
